import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { bugsnag } from 'lib/external/bugsnag';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getMaterialPickingEnabled } from 'lib/selectors/getMaterialPickingEnabled';
import usePickingSummary from 'lib/hooks/usePickingSummary';

/**
 * Builds the `materialCoverageByWoo` payload the embedded labor ticket form uses to
 * block submitting more parts than there is picked material to cover.
 *
 * The payload is keyed by the current WOO's `workOrderOperationRef` so the embed can
 * match it against `ticket.workOrderOperationRef`. For each gated material,
 * `available` is usePickingSummary's `remaining`, the same number the Materials
 * log shows: picked quantity minus the material already consumed by parts D365
 * has been or will be told about (submitted labor tickets, good + bad since
 * scrap consumes material; counted triage entries; and parts reported on the
 * order outside MM). The baseline
 * counts every ticket on the WOO, including the one open in the form; the embed
 * subtracts the parts that ticket already carries from the entered quantity, so
 * only the increase is checked against `available` (mm-integrations
 * labor-ticket-form getCoverageDemand).
 *
 * `availableNetOfTriage: true` tells the embed that `available` already takes off
 * the parts sent to Triage, which the Triage report tells D365 about. Where the
 * embed takes Triage scrap off good parts (rejects hidden), it then checks only the
 * parts the labor ticket itself will report, so a triaged part's material is
 * counted once, here, rather than on both sides. An embed that doesn't read the
 * flag checks the entered total, as before.
 *
 * Only materials D365 enforces are gated: those with `enforced !== false`, so data
 * from before the connector wrote the flag gates every material, as it always has.
 *
 * Once any material on the WOO has been picked, every gated material is sent,
 * including ones with nothing picked yet: an operator may start a job after picking
 * some of its materials, but cannot report parts until the rest are picked. When a
 * material is marked enforced, D365 rejects a report with nothing picked, so the
 * gate stays on even then. Only when no material is marked enforced (older data)
 * and nothing has been picked (e.g. the job was already running when picking
 * turned on) is there no coverage to enforce: sending it would block the operator
 * from submitting labor for parts produced before picking began.
 *
 * Returns { materialCoverageByWoo, ready }. materialCoverageByWoo is null when there
 * is no current WOO, when the WOO has no gated material requirements (feature off
 * for that job), or when nothing has been picked and no material is marked
 * enforced. `ready` is false until the WOO's
 * material data has loaded once, so the form is not opened without its check. A
 * failed read counts as ready: it is reported, and the form opens without the check
 * rather than blocking labor.
 *
 * @returns {{ materialCoverageByWoo: (Object|null), ready: boolean }}
 */
const useMaterialCoverageContext = () => {
  const materialPickingEnabled = useSelector(getMaterialPickingEnabled);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);

  const wooRef = currentWorkOrderOp?.workOrderOperationRef;
  const { materials, loaded, error } = usePickingSummary(wooRef);

  useEffect(() => {
    if (materialPickingEnabled && error) {
      bugsnag.notify(error, (event) => {
        event.addMetadata('materialCoverage', {
          workOrderOperationRef: wooRef,
        });
      });
    }
  }, [materialPickingEnabled, error, wooRef]);

  const ready = !materialPickingEnabled || !wooRef || loaded || Boolean(error);

  const materialCoverageByWoo = useMemo(() => {
    if (!materialPickingEnabled || !wooRef || !materials.length) {
      return null;
    }

    const gated = materials.filter((mat) => {
      return mat.enforced !== false;
    });
    if (!gated.length) {
      return null;
    }

    const anyEnforced = materials.some((mat) => {
      return mat.enforced === true;
    });
    const anyPicked = materials.some((mat) => {
      return mat.pickedQty > 0;
    });
    if (!anyEnforced && !anyPicked) {
      return null;
    }

    const coverageMaterials = gated.map((mat) => {
      const qtyPerUnit = mat.qtyPerUnit || 0;
      return {
        itemNumber: mat.itemNumber,
        // The embed renders `description` in its blocking message; itemNumber is
        // the display name until the connector supplies a real description.
        description: mat.itemNumber,
        qtyPerUnit,
        // Unit of measure from availableMaterials (e.g. "lb"), for display.
        unit: mat.unit || null,
        // Raw picked quantity, for display. Unlike `available` this never goes
        // negative, so the embed can show what the operator actually picked.
        pickedQty: mat.pickedQty,
        available: mat.remaining,
      };
    });

    return {
      [wooRef]: { materials: coverageMaterials, availableNetOfTriage: true },
    };
  }, [materialPickingEnabled, materials, wooRef]);

  return { materialCoverageByWoo, ready };
};

export default useMaterialCoverageContext;
